"use client"

import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/apiService';
import { DashboardHeader } from '../components/ui/dashboard-header';
import { DeviceStatsTable } from '../components/ui/device-stats-table';
import { Card, CardContent } from '../components/ui/card';
import { Server, Users, CheckCircle, Activity, Database } from 'lucide-react';
import { CustomOverlaySidebar } from '../components/ui/custom-overlay-sidebar';
import { isOnPremiseDeviceType } from '../utils/deviceType';
import { trialSerialSet, isTrialStat } from '../utils/trialMachines';
import { downloadCsv } from '../utils/csvExport';
import { useLogout } from '../hooks/useLogout';
import { useSidebar } from '../hooks/useSidebar';
import type { DeviceStats } from '../types';

// Per client–site, latest event_received_time vs 1 month (~30 days).
const OFFLINE_SITE_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000;
// Target sites per table page — a page can exceed this if a single Project
// has more sites than this, since a Project's sites are never split across pages.
const PAGE_TARGET_SIZE = 20;

const normalizeStat = (item: any): DeviceStats => ({
  ...item,
  additional_notes: item.additional_notes || item.additional || {},
  additional: item.additional || item.additional_notes || {},
});

const extractArray = (raw: any): any[] => {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.results)) return raw.results;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
};

const Dashboard: React.FC = () => {
  // Lightweight summary — all sites, small fields only. Feeds counts + dropdowns.
  const [stats, setStats] = useState<DeviceStats[]>([]);
  const [allMachines, setAllMachines] = useState<any[]>([]);
  const [trialSerials, setTrialSerials] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { sidebarOpen, closeSidebar, toggleSidebar } = useSidebar();
  const handleLogout = useLogout();

  const [filters, setFilters] = useState({
    client: "all",
    site: "all",
    deviceType: "onpremise" as "all" | "onpremise",
  });
  // Table defaults to active sites only; clicking "Offline Sites" switches this to show offline sites instead.
  const [siteView, setSiteView] = useState<"active" | "offline">("active");

  // Full-detail rows for whichever page is currently shown — pagination is
  // computed from the lightweight summary data (below) grouped by Project, so
  // a project's sites are never split across pages regardless of page size.
  const [tableRows, setTableRows] = useState<DeviceStats[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Server-computed sums for the Processing Metrics cards.
  const [processingMetrics, setProcessingMetrics] = useState({
    total_images_synced: 0,
    total_images_processed: 0,
    total_images_received: 0,
    total_tasks_processed: 0,
    total_tasks_received: 0,
  });

  const handleFiltersChange = (newFilters: { client: string; site: string; deviceType?: string }) => {
    setFilters({
      ...filters,
      ...newFilters,
      deviceType: (newFilters.deviceType === 'onpremise' ? 'onpremise' : 'all') as 'all' | 'onpremise',
    });
  };

  // Summary + machines — cheap, all rows, feeds counts and filter dropdowns.
  const fetchSummary = async () => {
    setLoading(true);
    try {
      const [machinesResponse, response] = await Promise.all([
        apiService.getMachines(),
        apiService.getDeviceStatsSummary(),
      ]);
      if (machinesResponse.success && Array.isArray(machinesResponse.data)) {
        setTrialSerials(trialSerialSet(machinesResponse.data));
        setAllMachines(machinesResponse.data);
      }

      if (response.success) {
        const list = extractArray(response.data).map(normalizeStat);
        setStats(list);
        setError(null);
      } else {
        setError(response.error || 'Failed to fetch stats');
      }
    } catch (err) {
      setError('An unexpected error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  // site_name -> project_name, from the (already-fetched, lightweight) machines list.
  const siteToProject = React.useMemo(() => {
    const map = new Map<string, string>();
    allMachines.forEach((m: any) => {
      if (m.site_name && m.project_name) {
        map.set(m.site_name, m.project_name);
      }
    });
    return map;
  }, [allMachines]);

  // Every site matching the current filters/view — derived from the cheap
  // summary data, used only to decide pagination, not rendered directly.
  const visibleSummarySites = React.useMemo(() => {
    const now = Date.now();
    let filtered = stats.filter((stat) => !isTrialStat(stat, trialSerials));

    if (filters.deviceType === "onpremise") {
      filtered = filtered.filter((stat) => isOnPremiseDeviceType(stat.device_type));
    }
    if (filters.client !== "all") {
      filtered = filtered.filter((stat) => stat.client_id === filters.client);
    }
    if (filters.site !== "all") {
      filtered = filtered.filter((stat) => stat.site_id === filters.site);
    }

    return filtered.filter((stat) => {
      const t = new Date(stat.event_received_time).getTime();
      if (Number.isNaN(t)) return false;
      const isOffline = now - t > OFFLINE_SITE_THRESHOLD_MS;
      return siteView === "offline" ? isOffline : !isOffline;
    });
  }, [stats, trialSerials, filters, siteView]);

  // Group-aware pages: each page is a list of site_ids, built by packing whole
  // Project groups (never split) up to ~PAGE_TARGET_SIZE sites per page.
  const pagedGroups = React.useMemo(() => {
    const groupMap = new Map<string | null, string[]>();
    visibleSummarySites.forEach((stat) => {
      const projectName = siteToProject.get(stat.site_id) ?? null;
      if (!groupMap.has(projectName)) groupMap.set(projectName, []);
      groupMap.get(projectName)!.push(stat.site_id);
    });

    // Named projects first (alphabetically), ungrouped sites last — matches
    // DeviceStatsTable's own project ordering.
    const sortedKeys = Array.from(groupMap.keys()).sort((a, b) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return a.localeCompare(b);
    });

    const pages: string[][] = [];
    let current: string[] = [];
    sortedKeys.forEach((key) => {
      const siteIds = groupMap.get(key)!;
      if (current.length > 0 && current.length + siteIds.length > PAGE_TARGET_SIZE) {
        pages.push(current);
        current = [];
      }
      current.push(...siteIds);
    });
    if (current.length > 0) pages.push(current);

    return pages;
  }, [visibleSummarySites, siteToProject]);

  // Full detail for whichever page is currently selected.
  const fetchTableData = useCallback(async () => {
    const siteIds = pagedGroups[Math.min(page - 1, Math.max(pagedGroups.length - 1, 0))] || [];
    setTableLoading(true);
    try {
      const response = await apiService.getDeviceStatsByIds(siteIds);
      if (response.success) {
        const list = extractArray(response.data).map(normalizeStat);
        setTableRows(list);
      }
    } catch {
      // Table-specific fetch failures don't need to blank the whole dashboard —
      // the summary/metrics calls report the general error state already.
    } finally {
      setTableLoading(false);
    }
  }, [pagedGroups, page]);

  useEffect(() => {
    setPage(1);
  }, [filters, siteView]);

  const fetchProcessingMetrics = useCallback(async () => {
    try {
      const response = await apiService.getProcessingMetrics({
        site_id: filters.site !== "all" ? filters.site : undefined,
        client_id: filters.client !== "all" ? filters.client : undefined,
        device_type: filters.deviceType !== "all" ? filters.deviceType : undefined,
      });
      if (response.success && response.data) {
        setProcessingMetrics(response.data);
      }
    } catch {
      // Leave prior totals in place on failure rather than zeroing them out.
    }
  }, [filters]);

  const handleRefresh = () => {
    fetchSummary();
    fetchTableData();
    fetchProcessingMetrics();
  };

  const handleExport = async () => {
    // Export needs full per-device detail across everything matching the
    // current filters — fetched on demand here, not kept in memory for the
    // page load itself.
    const response = await apiService.getDeviceStats({
      site_id: filters.site !== "all" ? filters.site : undefined,
      client_id: filters.client !== "all" ? filters.client : undefined,
    });
    if (!response.success) return;

    const exportStats = extractArray(response.data)
      .map(normalizeStat)
      .filter((stat) => !isTrialStat(stat, trialSerials))
      .filter((stat) => filters.deviceType !== "onpremise" || isOnPremiseDeviceType(stat.device_type));

    const groupedData: { [key: string]: any } = {};

    exportStats.forEach(stat => {
      const key = `${stat.client_id}-${stat.site_id}`;

      if (!groupedData[key]) {
        groupedData[key] = {
          client_id: stat.client_id,
          site_id: stat.site_id,
          start_time: stat.start_time,
          end_time: stat.end_time,
          total_images_processed: 0,
          total_images_synced: 0,
          total_images_pending: 0,
          record_count: 0,
          priorities: new Set(),
          tags: new Set(),
          error_count: 0,
        };
      }

      const group = groupedData[key];
      group.total_images_processed += stat.stats.images_processed_current || stat.stats.images_processed || 0;
      group.total_images_synced += stat.stats.patients_synced_current || 0;
      group.total_images_pending += stat.stats.images_sync_pending_current || stat.stats.image_sync_pending || 0;
      group.record_count += 1;

      if (new Date(stat.start_time ?? '') < new Date(group.start_time)) {
        group.start_time = stat.start_time;
      }
      if (new Date(stat.end_time ?? '') > new Date(group.end_time)) {
        group.end_time = stat.end_time;
      }

      if (stat.additional_notes?.priority) {
        group.priorities.add(stat.additional_notes.priority);
      }
      if (stat.additional_notes?.tags) {
        stat.additional_notes.tags.forEach(tag => group.tags.add(tag));
      }
      if (stat.additional_notes?.error_details) {
        group.error_count += 1;
      }
    });

    const headers = ['Client', 'Site', 'Start Time', 'End Time', 'Image Processed Count', 'Patient Synced Count', 'Sync Status', 'Record Count', 'Priorities', 'Tags', 'Error Count'];
    const csvContent = [
      headers.join(','),
      ...Object.values(groupedData).map((group: any) => {
        const syncStatus = group.total_images_pending === 0 ? 'Synced' : `${group.total_images_pending} Pending`;

        return [
          group.client_id,
          group.site_id,
          `"${new Date(group.start_time).toLocaleString()}"`,
          `"${new Date(group.end_time).toLocaleString()}"`,
          group.total_images_processed,
          group.total_images_synced,
          `"${syncStatus}"`,
          group.record_count,
          `"${Array.from(group.priorities).join('; ')}"`,
          `"${Array.from(group.tags).join('; ')}"`,
          group.error_count
        ].join(',');
      })
    ].join('\n');

    downloadCsv(csvContent, `event_received_${new Date().toISOString().split('T')[0]}.csv`);
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  useEffect(() => {
    fetchProcessingMetrics();
  }, [fetchProcessingMetrics]);

  // Trial machines are shown on their own page — keep them out of the dashboard's
  // filter dropdowns and site counts entirely.
  const nonTrialStats = React.useMemo(
    () => stats.filter((stat) => !isTrialStat(stat, trialSerials)),
    [stats, trialSerials]
  );

  const { activeSitesCount, offlineSitesCount } = React.useMemo(() => {
    const now = Date.now();
    // Always on-prem only, excluding trial machines (they have their own page).
    let base = stats.filter(
      (stat) => isOnPremiseDeviceType(stat.device_type) && !isTrialStat(stat, trialSerials)
    );

    if (filters.client !== "all") {
      base = base.filter((stat) => stat.client_id === filters.client);
    }
    if (filters.site !== "all") {
      base = base.filter((stat) => stat.site_id === filters.site);
    }

    const latestByKey = new Map<string, number>();
    for (const stat of base) {
      const key = `${stat.client_id}-${stat.site_id}`;
      const t = new Date(stat.event_received_time).getTime();
      if (Number.isNaN(t)) continue;
      const prev = latestByKey.get(key);
      if (prev === undefined || t > prev) latestByKey.set(key, t);
    }

    let active = 0;
    let offline = 0;
    latestByKey.forEach((t) => {
      if (now - t > OFFLINE_SITE_THRESHOLD_MS) offline += 1;
      else active += 1;
    });
    return { activeSitesCount: active, offlineSitesCount: offline };
  }, [stats, trialSerials, filters.client, filters.site]);

  const { total_images_synced, total_images_processed, total_images_received, total_tasks_processed, total_tasks_received } = processingMetrics;
  const imageSyncPercentage = total_images_processed > 0 ? Math.round((total_images_synced / total_images_processed) * 100) : 0;
  const imageProcessingPercentage = total_images_received > 0 ? Math.round((total_images_processed / total_images_received) * 100) : 0;
  const taskProcessingPercentage = total_tasks_received > 0 ? Math.round((total_tasks_processed / total_tasks_received) * 100) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ backgroundColor: "#0f0f0f", color: "#e2e8f0" }}>
        <h2>Loading dashboard...</h2>
      </div>
    );
  }

  const SectionHeader = ({ title }: { title: string }) => (
    <div className="flex items-center gap-3 mb-5">
      <span
        className="text-xs font-bold uppercase tracking-widest"
        style={{ color: "rgba(255, 255, 255, 0.4)" }}
      >
        {title}
      </span>
      <div className="flex-1 h-px" style={{ backgroundColor: "rgba(255, 255, 255, 0.06)" }} />
    </div>
  );

  const metricCardBase: React.CSSProperties = {
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
  };

  return (
    <div style={{ backgroundColor: "#0f0f0f", minHeight: "100vh" }}>
      <CustomOverlaySidebar isOpen={sidebarOpen} onClose={closeSidebar} />

      <div className="w-full min-h-screen">
        <DashboardHeader
          onLogout={handleLogout}
          onFiltersChange={handleFiltersChange}
          onExport={handleExport}
          stats={nonTrialStats}
          onSidebarToggle={toggleSidebar}
        />

        <div className="p-6 space-y-10">

          {/* ── Section 1: System Overview ── */}
          <div>
            <SectionHeader title="System Overview" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Active Sites */}
              <Card
                onClick={() => setSiteView("active")}
                className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-2xl cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #003433 0%, #005a58 100%)",
                  ...metricCardBase,
                  outline: siteView === "active" ? "2px solid #ffffff" : "none",
                  outlineOffset: "-2px",
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="rounded-xl p-2.5" style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}>
                      <Server className="h-5 w-5" style={{ color: "#ffffff" }} />
                    </div>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: "rgba(255, 255, 255, 0.12)", color: "#ffffff" }}
                    >
                      Stable
                    </span>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255, 255, 255, 0.55)" }}>
                    Active Sites
                  </div>
                  <div className="text-5xl font-bold leading-none" style={{ color: "#ffffff" }}>
                    {activeSitesCount}
                  </div>
                </CardContent>
              </Card>

              {/* Offline Sites */}
              <Card
                onClick={() => setSiteView("offline")}
                className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-2xl cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #004a48 0%, #006b69 100%)",
                  ...metricCardBase,
                  outline: siteView === "offline" ? "2px solid #ffffff" : "none",
                  outlineOffset: "-2px",
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="rounded-xl p-2.5" style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}>
                      <Users className="h-5 w-5" style={{ color: "#ffffff" }} />
                    </div>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: "rgba(255, 255, 255, 0.12)", color: "#ffffff" }}
                    >
                      Stale
                    </span>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255, 255, 255, 0.55)" }}>
                    No Data (1+ Month)
                  </div>
                  <div className="text-5xl font-bold leading-none" style={{ color: "#ffffff" }}>
                    {offlineSitesCount}
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>

          {/* ── Section 2: Processing Metrics ── */}
          <div>
            <SectionHeader title="Processing Metrics" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              {/* Patients Synced */}
              <Card
                className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-2xl cursor-pointer"
                style={{ background: "linear-gradient(135deg, #003d1e 0%, #006130 100%)", ...metricCardBase }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="rounded-xl p-2.5" style={{ backgroundColor: "rgba(110, 231, 183, 0.15)" }}>
                      <Database className="h-5 w-5" style={{ color: "#6ee7b7" }} />
                    </div>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: "rgba(110, 231, 183, 0.15)", color: "#6ee7b7" }}
                    >
                      {imageSyncPercentage}%
                    </span>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255, 255, 255, 0.55)" }}>
                    Patients Synced
                  </div>
                  <div className="text-3xl font-bold leading-none mb-1.5" style={{ color: "#ffffff" }}>
                    {total_images_synced.toLocaleString()}
                  </div>
                  <div className="text-sm mb-3" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                    of {total_images_processed.toLocaleString()} processed
                  </div>
                  <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}>
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(imageSyncPercentage, 100)}%`, backgroundColor: "#6ee7b7" }} />
                  </div>
                </CardContent>
              </Card>

              {/* Images Processed */}
              <Card
                className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-2xl cursor-pointer"
                style={{ background: "linear-gradient(135deg, #004d20 0%, #007540 100%)", ...metricCardBase }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="rounded-xl p-2.5" style={{ backgroundColor: "rgba(110, 231, 183, 0.15)" }}>
                      <CheckCircle className="h-5 w-5" style={{ color: "#6ee7b7" }} />
                    </div>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: "rgba(110, 231, 183, 0.15)", color: "#6ee7b7" }}
                    >
                      {imageProcessingPercentage}%
                    </span>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255, 255, 255, 0.55)" }}>
                    Images Processed
                  </div>
                  <div className="text-3xl font-bold leading-none mb-1.5" style={{ color: "#ffffff" }}>
                    {total_images_processed.toLocaleString()}
                  </div>
                  <div className="text-sm mb-3" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                    of {total_images_received.toLocaleString()} received
                  </div>
                  <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}>
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(imageProcessingPercentage, 100)}%`, backgroundColor: "#6ee7b7" }} />
                  </div>
                </CardContent>
              </Card>

              {/* Tasks Processed */}
              <Card
                className="border-0 relative overflow-hidden transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-2xl cursor-pointer"
                style={{ background: "linear-gradient(135deg, #005520 0%, #008540 100%)", ...metricCardBase }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="rounded-xl p-2.5" style={{ backgroundColor: "rgba(110, 231, 183, 0.15)" }}>
                      <Activity className="h-5 w-5" style={{ color: "#6ee7b7" }} />
                    </div>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-medium"
                      style={{ backgroundColor: "rgba(110, 231, 183, 0.15)", color: "#6ee7b7" }}
                    >
                      {taskProcessingPercentage}%
                    </span>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "rgba(255, 255, 255, 0.55)" }}>
                    Tasks Processed
                  </div>
                  <div className="text-3xl font-bold leading-none mb-1.5" style={{ color: "#ffffff" }}>
                    {total_tasks_processed.toLocaleString()}
                  </div>
                  <div className="text-sm mb-3" style={{ color: "rgba(255, 255, 255, 0.4)" }}>
                    of {total_tasks_received.toLocaleString()} received
                  </div>
                  <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}>
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(taskProcessingPercentage, 100)}%`, backgroundColor: "#6ee7b7" }} />
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>

          {/* ── Section 3: Site Activity ── */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-1">
                <SectionHeader title={siteView === "offline" ? "Site Activity — No Data (1+ Month)" : "Site Activity — Active Sites"} />
              </div>
              {siteView === "offline" && (
                <button
                  onClick={() => setSiteView("active")}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
                  style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "#94a3b8" }}
                >
                  ← Back to Active Sites
                </button>
              )}
            </div>
            {nonTrialStats.length > 0 && visibleSummarySites.length === 0 && !tableLoading && (
              <div
                className="mb-4 p-4 rounded-xl flex items-center gap-3"
                style={{ backgroundColor: "rgba(234, 179, 8, 0.1)", border: "1px solid rgba(234, 179, 8, 0.3)", color: "#eab308" }}
              >
                <span className="text-sm font-medium">
                  No {siteView} sites match the current filters. Try <strong>Clear Filters</strong> or switch views.
                </span>
              </div>
            )}
            <DeviceStatsTable
              stats={tableRows}
              loading={tableLoading}
              error={error}
              onRefresh={handleRefresh}
              machines={allMachines}
              defaultPageSize={1000}
            />
            {pagedGroups.length > 1 && (
              <div className="flex items-center justify-between mt-4 px-1">
                <span className="text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
                  Page {page} of {pagedGroups.length} ({visibleSummarySites.length} sites total)
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.05)",
                      color: page === 1 ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)",
                    }}
                  >
                    ← Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(pagedGroups.length, p + 1))}
                    disabled={page === pagedGroups.length}
                    className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.05)",
                      color: page === pagedGroups.length ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.7)",
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Dashboard;
